import { useState, useEffect } from "react";
import { TextField, Button, Typography, Paper, Snackbar, Alert, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useApp } from "../../context/AppContext";
import { useLanguage } from "../../context/LanguageContext";

export default function BasicDetailsForm(props) {
  const { user, fetchUser, updateUser, loading } = useApp();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    email: "",
    f_name: "",
    l_name: "",
    birth_date: "",
    country: "",
    city: "",
    postal_code: "",
    full_address: "",
  });
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Функция для маски даты YYYY-MM-DD
  const formatDateInput = (value) => {
    // Убираем все нецифровые символы
    const numbers = value.replace(/\D/g, '');
    
    // Ограничиваем длину до 8 цифр (YYYYMMDD)
    const limitedNumbers = numbers.slice(0, 8);
    
    // Форматируем в YYYY-MM-DD
    if (limitedNumbers.length >= 4) {
      const year = limitedNumbers.slice(0, 4);
      const month = limitedNumbers.slice(4, 6);
      const day = limitedNumbers.slice(6, 8);
      
      if (limitedNumbers.length >= 6) {
        return `${year}-${month}-${day}`;
      } else if (limitedNumbers.length >= 4) {
        return `${year}-${month}`;
      }
    }
    
    return limitedNumbers;
  };

  // Функция для валидации даты
  const validateDate = (dateString) => {
    if (!dateString) return false;
    
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return false;
    
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    // Проверяем, что дата валидна и не в будущем
    const isValidDate = date.getFullYear() === year &&
                       date.getMonth() === month - 1 &&
                       date.getDate() === day;
    
    const isNotFuture = date <= new Date();
    const isNotTooOld = year >= 1900;
    
    return isValidDate && isNotFuture && isNotTooOld;
  };

  // Функция для обработки потери фокуса на поле даты
  const handleDateBlur = (e) => {
    const { name, value } = e.target;
    
    if (name === 'birth_date' && value) {
      if (!validateDate(value)) {
        setErrors(prev => ({ 
          ...prev, 
          birth_date: "Please enter a valid date in YYYY-MM-DD format (e.g., 2025-07-18)." 
        }));
      }
    }
  };

  // Функция для обработки нажатий клавиш в поле даты
  const handleDateKeyDown = (e) => {
    const { key } = e;
    
    // Разрешаем только цифры, дефис, backspace, delete, стрелки
    const allowedKeys = [
      '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
      '-', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Tab', 'Enter'
    ];
    
    if (!allowedKeys.includes(key)) {
      e.preventDefault();
    }
  };

  // Функция для закрытия уведомления
  const handleCloseNotification = () => {
    setNotification({ show: false, message: '', type: '' });
  };

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = await fetchUser();
        if (userData) {
          setFormData(userData);
        }
      } catch (error) {
        console.error("Failed to load user data:", error);
      }
    };

    // Делаем запрос при каждом переходе на страницу
    loadUserData();
  }, []); // Запускаем только один раз при монтировании

  const formDataChange = (e) => {
    const { name, value } = e.target;
    
    // Специальная обработка для поля даты рождения
    if (name === 'birth_date') {
      const formattedValue = formatDateInput(value);
      setFormData((prevData) => ({
        ...prevData,
        [name]: formattedValue,
      }));
      
      // Очищаем ошибку даты при вводе
      if (errors.birth_date) {
        setErrors(prev => ({ ...prev, birth_date: null }));
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleSaveChanges = async (e) => {
    e.preventDefault();
    console.log("Data being validated:", formData);    
    // Очищаем предыдущие уведомления
    setNotification({ show: false, message: '', type: '' });
    
    if (validate()) {
      setIsSubmitting(true);
      props.setActiveTab(2);
      try {
        formData.verif_step = "1";
        await updateUser(formData);
        
        // Показываем уведомление об успехе
        setNotification({
          show: true,
          message: 'Your profile has been updated successfully!',
          type: 'success'
        });
        
        // Скрываем уведомление через 5 секунд
        setTimeout(() => {
          setNotification({ show: false, message: '', type: '' });
        }, 5000);
        
      } catch (err) {
        console.log(err.response?.data);
        console.log("Form has errors.");
        
        // Показываем уведомление об ошибке
        setNotification({
          show: true,
          message: 'Failed to update profile. Please try again.',
          type: 'error'
        });
        
        // Скрываем уведомление через 5 секунд
        setTimeout(() => {
          setNotification({ show: false, message: '', type: '' });
        }, 5000);
      } finally {
        setIsSubmitting(false);
      }
    } 
  };

  const validate = () => {
    let tempErrors = {};
    if (formData.email.length === 0) tempErrors.email = "Email is required.";
    if (!formData.f_name) tempErrors.f_name = "First name is required.";
    if (!formData.l_name) tempErrors.l_name = "Last name is required.";
    
    // Валидация даты рождения
    if (!formData.birth_date) {
      tempErrors.birth_date = "Birth date is required.";
    } else if (!validateDate(formData.birth_date)) {
      tempErrors.birth_date = "Please enter a valid date in YYYY-MM-DD format (e.g., 2025-07-18).";
    }
    
    if (!formData.country) tempErrors.country = "Country is required.";
    if (!formData.city) tempErrors.city = "City is required.";
    if (!formData.postal_code)
      tempErrors.postal_code = "Postal code is required.";
    if (!formData.full_address)
      tempErrors.full_address = "Full address is required.";

    setErrors(tempErrors);
    // Возвращаем true, если ошибок нет, и false, если есть
    return Object.keys(tempErrors).length === 0;
  };

  return (
    <Paper elevation={0} className="details-card card" >
      {/* Уведомление */}
      <Snackbar
        open={notification.show}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          icon={notification.type === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
          sx={{
            backgroundColor: notification.type === 'success' ? '#22c55e' : '#ef4444',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <div className="details-layout">
        {/* Левая колонка с заголовком */}
        <div className="details-title-section">
          <Typography variant="h6" className="details-title">
            {t('basicDetails')}
          </Typography>
        </div>

        {/* Правая колонка с формой */}
        <div className="details-form-section">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Typography variant="h6">{t('loadingUserData')}</Typography>
            </div>
          ) : (
            <form
              noValidate
              className="details-layout"
              onSubmit={handleSaveChanges}
            >
              <div className="form-fields-wrapper">
                {/* Поле Email */}
                <div className="form-row form-row-full">
                  <TextField
                    fullWidth
                    label={t('emailAddress')}
                    variant="outlined"
                    name="email"
                    value={formData.email || ""}
                    onChange={formDataChange}
                    className="custom-textfield"
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                </div>

              {/* Имя и Фамилия */}
              <div className="form-row form-row-half">
                <TextField
                  fullWidth
                  label={t('firstName')}
                  variant="outlined"
                  name="f_name"
                  value={formData.f_name || ""}
                  onChange={formDataChange}
                  className="custom-textfield"
                  error={!!errors.f_name}
                  helperText={errors.f_name}
                  required
                />
              </div>

              <div className="form-row form-row-half">
                <TextField
                  fullWidth
                  label={t('lastName')}
                  variant="outlined"
                  name="l_name"
                  value={formData.l_name || ""}
                  onChange={formDataChange}
                  className="custom-textfield"
                  error={!!errors.l_name}
                  helperText={errors.l_name}
                  required
                />
              </div>

              {/* Дата рождения и Страна */}
              <div className="form-row form-row-half">
                <TextField
                  fullWidth
                  label={t('dateOfBirth')}
                  variant="outlined"
                  className="custom-textfield"
                  name="birth_date"
                  value={formData.birth_date || ""}
                  onChange={formDataChange}
                  onBlur={handleDateBlur}
                  onKeyDown={handleDateKeyDown}
                  error={!!errors.birth_date}
                  helperText={errors.birth_date || t('dateFormatHint')}
                  placeholder="YYYY-MM-DD"
                  required
                />
              </div>

              <div className="form-row form-row-half">
                <TextField
                  fullWidth
                  label={t('country')}
                  variant="outlined"
                  name="country"
                  value={formData.country || ""}
                  onChange={formDataChange}
                  className="custom-textfield"
                  error={!!errors.country}
                  helperText={errors.country}
                  required
                />
              </div>

              {/* Город и Индекс */}
              <div className="form-row form-row-half">
                <TextField
                  fullWidth
                  label={t('city')}
                  variant="outlined"
                  className="custom-textfield"
                  name="city"
                  value={formData.city || ""}
                  onChange={formDataChange}
                  error={!!errors.city}
                  helperText={errors.city}
                  required
                />
              </div>

              <div className="form-row form-row-half">
                <TextField
                  fullWidth
                  label={t('postalCode')}
                  variant="outlined"
                  className="custom-textfield"
                  name="postal_code"
                  value={formData.postal_code || ""}
                  onChange={formDataChange}
                  error={!!errors.postal_code}
                  helperText={errors.postal_code}
                  required
                />
              </div>

              {/* Полный адрес */}
              <div className="form-row form-row-full">
                <TextField
                  fullWidth
                  label={t('fullAddress')}
                  variant="outlined"
                  className="custom-textfield"
                  name="full_address"
                  value={formData.full_address || ""}
                  onChange={formDataChange}
                  error={!!errors.full_address}
                  helperText={errors.full_address}
                  required
                />
              </div>

              {/* Кнопка */}
              <div className="form-actions">
                <Button
                  variant="contained"
                  size="large"
                  className="save-button"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t('saveChanges')}
                </Button>
              </div>
            </div>
          </form>
          )}
        </div>
      </div>
    </Paper>
  );
}
